import React, { useState, useRef, useEffect, useContext } from "react";
import { TabContent, TabPane, Nav, NavItem, NavLink, Row, Col } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import PropTypes from "prop-types";
import moment from "moment";
import cx from "classnames";

import Image from "../../ui/image";
import TableFormatter from "../../ui/TableFormatter";
import { Icon } from "../../ui/icon";

import coffeeBreakPng from "../../../assets/images/dhp-coffee-break.png";
import coffeeBreakWebp from "../../../assets/images/dhp-coffee-break.webp";
import global from "../../../scss/dhp.scss";

import {
  fetchAnalyticsGroupByDetails,
  fetchAnalyticsGroupByList,
  fetchAnalyticsVisitorDetails,
  fetchAnalyticsVisitorList,
} from "../../../store/actions/documentActions";
import {
  ANALYTICS_DATA_TYPE_BLOCK,
  ANALYTICS_DATA_TYPE_COUNTRY,
  ANALYTICS_DATA_TYPE_LINK,
  ANALYTICS_DATA_TYPE_TEXT,
  ANALYTICS_DATA_TYPE_VISITOR,
  BLOCKS,
  COUNTRIES,
  LINKS,
  TYPE_INFOGRAPHIC,
  TYPE_PRESENTATION,
  TYPE_PROPOSAL,
  VIEWERS,
} from "../../../constants/editor";
import { getRandomString, removeDuplicatesFromArrayOfObject, setGMTAtSession } from "../../../_helpers/utils";
import { EditorContext } from "../../../containers/editor/EditorLayout";

let style = Object.assign({}, global);

const AnalyticsLoader = ({ row, col, analytics, colWidth = "33.333333%" }) => {
  const loaderHeight = `${22}px`;

  return (
    <div className={cx(style["modal-tab"], style["row"], style["m-0"], style["AnalyticsLoader"])}>
      <div className={cx(style["col-3"], style["p-0"], style["left"])}>
        <Nav className={cx(style["nav-pills"], style["flex-column"], style["h-100"])}>
          {analytics.meta.navConfig
            .filter(TYPE => TYPE.DISPLAY)
            .map(TYPE => (
              <NavItem key={`loader-nav-item-${TYPE.SLUG}`}>
                <NavLink className={cx(style["skeleton-loader-area"])}>
                  <div
                    className={cx(
                      style["d-flex"],
                      style["align-items-center"],
                      style["justify-content-between"],
                      style["w-100"],
                      style["mb-1"],
                      style["mt-1"]
                    )}>
                    <div className={cx(style["d-flex"], style["align-items-center"])}>
                      <div
                        className={cx(style["loader-item"], style["ms-2"])}
                        style={{ width: `${30}px`, height: loaderHeight }}></div>
                      <div
                        className={cx(style["loader-item"], style["ms-2"])}
                        style={{ width: `${100}px`, height: loaderHeight }}></div>
                    </div>
                    <div
                      className={cx(style["loader-item"], style["ms-2"])}
                      style={{ width: `${30}px`, height: loaderHeight }}></div>
                  </div>
                </NavLink>
              </NavItem>
            ))}
        </Nav>
      </div>
      <div className={cx(style["col-9"], style["p-4"], style["right"])}>
        <div className={style["table-info-wrap"]}>
          <div className={cx(style["table-responsive-custom"])}>
            <table className={cx(style["table"], style["table-borderless"], style["thumb-active"], style["d-block"])}>
              <thead>
                <tr>
                  {[...Array(col)].map((_, i) => (
                    <th
                      key={`loader-thead-tr-new-${i}`}
                      className={cx(style["skeleton-loader-area"])}
                      style={{ width: colWidth }}>
                      <span className={style["loader-item"]} style={{ height: loaderHeight }}></span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(row)].map((_, i) => (
                  <tr key={`loader-tbody-tr-${i}`} className={cx(style["skeleton-loader-area"])}>
                    {[...Array(col)].map((_, i) => (
                      <td
                        key={`loader-tbody-td-${i}`}
                        className={cx(style["d-flex"], style["align-items-center"])}
                        style={{ width: colWidth }}>
                        <span className={style["loader-item"]} style={{ height: loaderHeight }}></span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const AnalyticsModal = ({ document: data, _helpers }) => {
  let fetchIntervalId;
  const FETCH_INTERVAL = 1000 * 30; // 30 second
  const dispatch = useDispatch();
  const { id: documentId } = useParams();
  const { documentType, blockNodes } = useContext(EditorContext);
  const userInfo = useSelector(state => state?.auth?.user);
  const isBlockStructureDoc = [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType);
  const isSlideStructureDoc = [TYPE_PRESENTATION].includes(documentType);
  const BLOCK_SLIDE_OR_PAGE_SINGULAR = isBlockStructureDoc ? "Block" : isSlideStructureDoc ? "Slide" : "Page";
  const BLOCK_SLIDE_OR_PAGE_PLURAL = isBlockStructureDoc ? "Blocks" : isSlideStructureDoc ? "Slides" : "Pages";

  const [tabType, setTabType] = useState(VIEWERS.SLUG);
  const [viewType, setViewType] = useState("group");
  const [isLoading, setIsLoading] = useState(true);
  const [noAnalytics, setNoAnalytics] = useState(false);

  const [visitorList, setVisitorList] = useState([]);
  const [blockList, setBlockList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [linkList, setLinkList] = useState([]);

  const [visitorCount, setVisitorCount] = useState(0);
  const [blockCount, setBlockCount] = useState(0);
  const [countryCount, setCountryCount] = useState(0);
  const [linkCount, setLinkCount] = useState(0);

  const analytics = {
    meta: {
      navConfig: [
        { ...VIEWERS, ICON: "ui-viewers", DATA: visitorList, COUNT: visitorCount, DISPLAY: true },
        {
          ...BLOCKS,
          ICON: "ui-blocks",
          DATA: blockList,
          COUNT: blockCount,
          NAME: BLOCK_SLIDE_OR_PAGE_PLURAL,
          DISPLAY: true,
        },
        { ...COUNTRIES, ICON: "ui-maps", DATA: countryList, COUNT: countryCount, DISPLAY: true },
        { ...LINKS, ICON: "ui-link", DATA: linkList, COUNT: linkCount, DISPLAY: linkCount > 0 },
      ],

      tableConfig: {
        [VIEWERS.SLUG]: {
          group: [
            {
              name: "Name",
              slug: "visitor_name",
              type: ANALYTICS_DATA_TYPE_VISITOR,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Country",
              slug: "country_name",
              type: ANALYTICS_DATA_TYPE_COUNTRY,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Last Viewed",
              slug: "last_viewed",
              type: ANALYTICS_DATA_TYPE_TEXT,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Time Spent",
              slug: "time_spent",
              type: ANALYTICS_DATA_TYPE_TEXT,
              display: true,
              arrowRequired: true,
              headerArrowRequired: false,
            },
          ],
          detail: [
            {
              name: "Name",
              slug: "visitor_name",
              type: ANALYTICS_DATA_TYPE_VISITOR,
              display: true,
              arrowRequired: false,
              headerArrowRequired: true,
            },
            {
              name: BLOCK_SLIDE_OR_PAGE_SINGULAR,
              slug: "block_name",
              type: ANALYTICS_DATA_TYPE_BLOCK,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Views",
              slug: "views",
              type: ANALYTICS_DATA_TYPE_TEXT,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Time Spent",
              slug: "time_spent",
              type: ANALYTICS_DATA_TYPE_TEXT,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
          ],
        },

        [BLOCKS.SLUG]: {
          group: [
            {
              name: BLOCK_SLIDE_OR_PAGE_SINGULAR,
              slug: "block_name",
              type: ANALYTICS_DATA_TYPE_BLOCK,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Views",
              slug: "views",
              type: ANALYTICS_DATA_TYPE_TEXT,
              display: true,
              arrowRequired: true,
              headerArrowRequired: false,
            },
          ],
          detail: [
            {
              name: BLOCK_SLIDE_OR_PAGE_SINGULAR,
              slug: "block_name",
              type: ANALYTICS_DATA_TYPE_BLOCK,
              display: true,
              arrowRequired: false,
              headerArrowRequired: true,
            },
            {
              name: "Name",
              slug: "visitor_name",
              type: ANALYTICS_DATA_TYPE_VISITOR,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Country",
              slug: "country_name",
              type: ANALYTICS_DATA_TYPE_COUNTRY,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Views",
              slug: "views",
              type: ANALYTICS_DATA_TYPE_TEXT,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
          ],
        },

        [COUNTRIES.SLUG]: {
          group: [
            {
              name: "Country",
              slug: "country_name",
              type: ANALYTICS_DATA_TYPE_COUNTRY,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Views",
              slug: "views",
              type: ANALYTICS_DATA_TYPE_TEXT,
              display: true,
              arrowRequired: true,
              headerArrowRequired: false,
            },
          ],
          detail: [
            {
              name: "Country",
              slug: "country_name",
              type: ANALYTICS_DATA_TYPE_COUNTRY,
              display: true,
              arrowRequired: false,
              headerArrowRequired: true,
            },
            {
              name: "Name",
              slug: "visitor_name",
              type: ANALYTICS_DATA_TYPE_VISITOR,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Views",
              slug: "views",
              type: ANALYTICS_DATA_TYPE_TEXT,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
          ],
        },

        [LINKS.SLUG]: {
          group: [
            {
              name: "Link",
              slug: "link",
              type: ANALYTICS_DATA_TYPE_LINK,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Clicks",
              slug: "clicks",
              type: ANALYTICS_DATA_TYPE_TEXT,
              display: true,
              arrowRequired: true,
              headerArrowRequired: false,
            },
          ],
          detail: [
            {
              name: "Link",
              slug: "link",
              type: ANALYTICS_DATA_TYPE_LINK,
              display: true,
              arrowRequired: false,
              headerArrowRequired: true,
            },
            {
              name: "Name",
              slug: "visitor_name",
              type: ANALYTICS_DATA_TYPE_VISITOR,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
            {
              name: "Clicks",
              slug: "clicks",
              type: ANALYTICS_DATA_TYPE_TEXT,
              display: true,
              arrowRequired: false,
              headerArrowRequired: false,
            },
          ],
        },
      },
    },

    helpers: {
      formatDateTime: epochTime => {
        if (!userInfo?.time_zone) return;
        const dateFormat = {
          "DD-MM-YYYY": "ddd DD MMM",
          "MM-DD-YYYY": "ddd MMM DD",
        };

        const defaultFormats = {
          "date-time": `${dateFormat[userInfo?.date_format] || "MMM ddd DD"}, yyyy ${
            userInfo?.time_format?.substring(0, 2) == 24 ? "HH:mm" : "hh:mm A"
          }`,
        };

        const applicableFormat = defaultFormats["date-time"];

        const gmtValue = setGMTAtSession(userInfo?.time_zone);
        const resultDate = moment(epochTime * 1000)
          .utcOffset(gmtValue)
          .format(applicableFormat);

        return resultDate;
      },

      formatDuration: ms => {
        const seconds = Math.floor(ms / 1000);
        const units = [
          { label: "day", value: 86400 },
          { label: "hr", value: 3600 },
          { label: "min", value: 60 },
          { label: "sec", value: 1 },
        ];

        let remaining = seconds;
        const parts = [];

        for (const u of units) {
          const amount = Math.floor(remaining / u.value);
          if (amount > 0) {
            parts.push(`${amount} ${u.label}${amount > 1 ? "" : ""}`);
            remaining %= u.value;
          }
        }

        return parts.length > 0 ? parts.join(" ") : "0 sec";
      },

      blockDetailsGenerator: blockId => {
        const data = { name: "", html: "", dimension: { w: 0, h: 0 } };

        // block name
        const targetBlockObj = blockNodes.find(e => e.blockId === blockId);
        if (!targetBlockObj) {
          return data;
        }

        const blockName =
          targetBlockObj?.blockName || `${BLOCK_SLIDE_OR_PAGE_SINGULAR} ${targetBlockObj?.blockIdx + 1}`;

        // block html
        const uniqueId = getRandomString(8);
        const sandBox = document.createElement("div");
        sandBox.innerHTML = document.getElementById(blockId).outerHTML;
        sandBox.querySelectorAll("[id]").forEach(elm => (elm.id = `copy-${elm.id}-unique-${uniqueId}`));
        sandBox.querySelector(".dhp-page-block").style.transform = ""; // remove transform styles (if any)
        const blockHtml = sandBox.innerHTML;

        // block dimension
        const blockNode = document.getElementById(blockId);
        const blockDimension = { w: parseFloat(blockNode.style.width), h: parseFloat(blockNode.style.height) };

        return { name: blockName, html: blockHtml, dimension: blockDimension };
      },

      linkGenerator: ({ analyticsLinks, type }) => {
        const links = [];
        const linkFinder = (array, url) => array.find(e => e.link === url);

        if (type === "group") {
          const physicalLinks = document.querySelectorAll(
            `.dhp-page-widget[data-hyperlink-type="Website"], .dhp-page-widget[data-hyperlink-type="Email"]`
          );

          // for links that are present in DOM
          physicalLinks.forEach(widget => {
            const { hyperlinkUrl: physicalUrl, hyperlinkType: urlType } = widget.dataset;
            const isAnalyticsPresent = linkFinder(analyticsLinks, physicalUrl) || false;
            links.push({
              link: physicalUrl || "",
              clicks: isAnalyticsPresent?.clicks || "",
              link_type: urlType || false,
              is_analytics_present: isAnalyticsPresent,
              is_deleted: false,
            });
          });

          // for links that are not present in DOM (once available in DOM, now sowing in analytics data)
          analyticsLinks.forEach(e => {
            const isLinkFoundPhysically = linkFinder(links, e.link) || false;
            if (!isLinkFoundPhysically) {
              links.push({
                link: e.link,
                clicks: e.clicks || "",
                link_type: false, // link_type is unkown as not available in DOM
                is_analytics_present: true,
                is_deleted: true,
              });
            }
          });

          // filter out duplicate links
          const filteredLinks = removeDuplicatesFromArrayOfObject({ arrayOfObj: links, keyToCompare: "link" });

          return {
            links: filteredLinks,
            total: filteredLinks.length,
          };
        }

        if (type === "detail") {
          analyticsLinks.forEach(e => {
            const isLinkFoundPhysically = document.querySelector(`.dhp-page-widget[data-hyperlink-url="${e.link}"]`);
            links.push({
              link: e.link || "",
              visitor_name: e.visitor_name || "",
              visitor_image: e.visitor_image || "",
              visitor_email: e.visitor_email || "",
              clicks: e.clicks || "",
              link_type: isLinkFoundPhysically?.dataset?.hyperlinkType || false, // link_type is unkown as not available in DOM
              is_analytics_present: true,
              is_deleted: !isLinkFoundPhysically,
            });
          });

          return {
            links,
            total: links.length,
          };
        }
      },
    },

    fetchListData: {
      visitor: () => {
        dispatch(fetchAnalyticsVisitorList({ document_id: documentId })).then(resp => {
          // console.log("visitor list ---", resp);
          if (resp?.status === 200) {
            const { analytics_data, total_count = 0 } = resp?.data?.data ?? { analytics_data: [], total_count: 0 };

            const data = [];
            analytics_data?.forEach(entry => {
              data.push({
                visitor_name: entry.name || entry.email || "",
                visitor_email: entry.email || "",
                visitor_image: entry.profile_image || "",
                visitor_reference_id: entry.reference_id || "",
                last_viewed: analytics.helpers.formatDateTime(entry.last_visit) || "",
                time_spent: analytics.helpers.formatDuration(entry.total_duration) || "",
                country_name: entry.country || "",
                country_code: entry.country_code || "",
                uniqueHash: crypto.randomUUID(),
              });
            });

            setVisitorList(data);
            setVisitorCount(total_count);
            setNoAnalytics(total_count === 0);
            setIsLoading(false);
          }
        });
      },

      block: () => {
        dispatch(fetchAnalyticsGroupByList({ document_id: documentId, group_by: BLOCKS.API_NAME })).then(resp => {
          // console.log("block list ---", resp);
          if (resp?.status === 200) {
            const { analytics_data, total_count = 0 } = resp?.data?.data ?? { analytics_data: [], total_count: 0 };

            const data = [];
            analytics_data?.forEach(entry => {
              const {
                name: blockName,
                html: blockHtml,
                dimension: blockDimension,
              } = analytics.helpers.blockDetailsGenerator(entry.block_id);

              data.push({
                block_id: entry.block_id || "",
                block_name: blockName || "",
                block_html: blockHtml || "",
                block_dimension: blockDimension || "",
                views: entry.views || "",
                time_spent: analytics.helpers.formatDuration(entry.duration) || "",
                uniqueHash: crypto.randomUUID(),
              });
            });

            setBlockList(data);
            setBlockCount(total_count);
          }
        });
      },

      country: () => {
        dispatch(fetchAnalyticsGroupByList({ document_id: documentId, group_by: COUNTRIES.API_NAME })).then(resp => {
          // console.log("country list ---", resp);
          if (resp?.status === 200) {
            const { analytics_data, total_count = 0 } = resp?.data?.data ?? { analytics_data: [], total_count: 0 };

            const data = [];
            analytics_data?.forEach(entry => {
              data.push({
                country_name: entry.country || "",
                country_code: entry.country_code || "",
                views: entry.views || "",
                uniqueHash: crypto.randomUUID(),
              });
            });

            setCountryList(data);
            setCountryCount(total_count);
          }
        });
      },

      link: () => {
        dispatch(fetchAnalyticsGroupByList({ document_id: documentId, group_by: LINKS.API_NAME })).then(resp => {
          // console.log("link list ---", resp);
          if (resp?.status === 200) {
            const { analytics_data, total_count = 0 } = resp?.data?.data ?? { analytics_data: [], total_count: 0 };

            const data = [];
            analytics_data?.forEach(entry => {
              data.push({
                link: entry.link || "",
                clicks: entry.clicks || "",
                uniqueHash: crypto.randomUUID(),
              });
            });

            const processedLinkData = analytics.helpers.linkGenerator({ analyticsLinks: data, type: "group" });
            setLinkList(processedLinkData.links);
            setLinkCount(processedLinkData.total);
          }
        });
      },
    },

    fetchAllData: () => {
      analytics.fetchListData.visitor();
      analytics.fetchListData.block();
      analytics.fetchListData.country();
      analytics.fetchListData.link();
    },

    periodicFetchAllData: () => {
      fetchIntervalId = setInterval(() => {
        analytics.fetchAllData();
      }, FETCH_INTERVAL);
    },

    garbageCollection: () => {
      clearInterval(fetchIntervalId);
      fetchIntervalId = null;
    },

    switchTab: tab => {
      setTabType(tab);
      setViewType("group");
    },
  };

  useEffect(() => {
    setIsLoading(true);
    analytics.fetchAllData();
    analytics.periodicFetchAllData();

    return () => {
      // cleanup
      analytics.garbageCollection();
    };
  }, []);

  return (
    <React.Fragment>
      <div className={style["modal-header"]}>
        <h4 className={cx(style["fw-7"], style["mb-0"])}>Analytics</h4>
        <Link to="#" className={cx(style["cross-modal"], style["rounded"])} onClick={_helpers?.modal?.toggle}>
          <Icon icon="ui-close" />
        </Link>
      </div>

      {isLoading && <AnalyticsLoader row={4} col={3} analytics={analytics} />}

      {!isLoading && noAnalytics && (
        <div className={cx(style["not-found-wrap"], style["mt-3"])}>
          <Image WebpImage={coffeeBreakWebp} PngImage={coffeeBreakPng} altText="No records" />
          <h6>
            You do not have any views on this <br /> document yet.
          </h6>
        </div>
      )}

      {!isLoading && !noAnalytics && (
        <div className={cx(style["modal-tab"], style["row"], style["m-0"])}>
          <div className={cx(style["col-3"], style["p-0"])}>
            <Nav className={cx(style["nav-pills"], style["flex-column"], style["h-100"])}>
              {analytics.meta.navConfig
                .filter(TYPE => TYPE.DISPLAY)
                .map(TYPE => (
                  <NavItem key={`nav-item-${TYPE.SLUG}`}>
                    <NavLink
                      className={cx({ [style["active"]]: tabType === TYPE.SLUG })}
                      onClick={() => analytics.switchTab(TYPE.SLUG)}>
                      <Icon icon={TYPE.ICON} /> {TYPE.NAME}
                      <span className={style["numberof-shared"]}>{TYPE.COUNT}</span>
                    </NavLink>
                  </NavItem>
                ))}
            </Nav>
          </div>
          <div className={cx(style["col-9"], style["p-0"])}>
            <TabContent activeTab={tabType}>
              {analytics.meta.navConfig
                .filter(TYPE => TYPE.SLUG === tabType && TYPE.DISPLAY)
                .map(TYPE => (
                  <TabPane key={`tab-pane-${TYPE.SLUG}`} tabId={TYPE.SLUG} className={style[TYPE.SLUG]}>
                    <Row>
                      <Col sm="12">
                        <div className={cx(style["content-wrap"], style[TYPE.SLUG])}>
                          <ViewersSection
                            documentId={documentId}
                            config={analytics.meta.tableConfig}
                            helpers={analytics.helpers}
                            tabType={tabType}
                            viewType={viewType}
                            setViewType={setViewType}
                            data={TYPE.DATA}
                          />
                        </div>
                      </Col>
                    </Row>
                  </TabPane>
                ))}
            </TabContent>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

const ViewersSection = ({ documentId, config, helpers, tabType, viewType, setViewType, data }) => {
  const tableRef = useRef(null);
  const dispatch = useDispatch();
  const [state, setState] = useState();
  const [selectedData, setSelectedData] = useState();
  const timeFormat = useSelector(state => state?.auth?.user?.time_format);

  const analytics = {
    meta: { config },
    helpers: helpers,

    fetchDetailListData: {
      visitor: ({ selectedData, config }) => {
        dispatch(
          fetchAnalyticsVisitorDetails({ document_id: documentId, reference_id: selectedData?.visitor_reference_id })
        ).then(resp => {
          // console.log("visitor details ---", resp);
          if (resp?.status === 200) {
            const { analytics_data } = resp?.data?.data ?? { analytics_data: [] };

            const data = [];
            analytics_data?.forEach(entry => {
              const {
                name: blockName,
                html: blockHtml,
                dimension: blockDimension,
              } = analytics.helpers.blockDetailsGenerator(entry.block_id);

              data.push({
                visitor_name: entry.name || entry.email || "",
                visitor_email: entry.email || "",
                visitor_image: entry.profile_image || "",
                block_id: entry.block_id || "",
                block_name: blockName || "",
                block_html: blockHtml || "",
                block_dimension: blockDimension || "",
                views: entry.views || 0,
                time_spent: analytics.helpers.formatDuration(entry.duration) || "",
                uniqueHash: crypto.randomUUID(),
              });
            });

            setState({ config, data });
          }
        });
      },

      block: ({ selectedData, config }) => {
        dispatch(fetchAnalyticsGroupByDetails({ document_id: documentId, block_id: selectedData?.block_id })).then(
          resp => {
            // console.log("block details ---", resp);
            if (resp?.status === 200) {
              const { analytics_data } = resp?.data?.data ?? { analytics_data: [] };

              const data = [];
              analytics_data?.forEach(entry => {
                const {
                  name: blockName,
                  html: blockHtml,
                  dimension: blockDimension,
                } = analytics.helpers.blockDetailsGenerator(entry.block_id);

                data.push({
                  block_id: entry.block_id || "",
                  block_name: blockName || "",
                  block_html: blockHtml || "",
                  block_dimension: blockDimension || "",
                  views: entry.views || "",
                  visitor_name: entry.name || entry.email || "",
                  visitor_email: entry.email || "",
                  visitor_image: entry.profile_image || "",
                  country_name: entry.country || "",
                  country_code: entry.country_code || "",
                  uniqueHash: crypto.randomUUID(),
                });
              });

              setState({ config, data });
            }
          }
        );
      },

      country: ({ selectedData, config }) => {
        dispatch(fetchAnalyticsGroupByDetails({ document_id: documentId, country: selectedData?.country_code })).then(
          resp => {
            // console.log("country details ---", resp);
            if (resp?.status === 200) {
              const { analytics_data } = resp?.data?.data ?? { analytics_data: [] };

              const data = [];
              analytics_data?.forEach(entry => {
                data.push({
                  country_name: entry.country || "",
                  country_code: entry.country_code || "",
                  views: entry.views || "",
                  visitor_name: entry.name || entry.email || "",
                  visitor_email: entry.email || "",
                  visitor_image: entry.profile_image || "",
                  uniqueHash: crypto.randomUUID(),
                });
              });

              setState({ config, data });
            }
          }
        );
      },

      link: ({ selectedData, config }) => {
        dispatch(fetchAnalyticsGroupByDetails({ document_id: documentId, link: selectedData?.link })).then(resp => {
          // console.log("link details ---", resp);
          if (resp?.status === 200) {
            const { analytics_data } = resp?.data?.data ?? { analytics_data: [] };

            const data = [];
            analytics_data?.forEach(entry => {
              data.push({
                link: entry.link || "",
                clicks: entry.clicks || "",
                visitor_name: entry.name || entry.email || "",
                visitor_email: entry.email || "",
                visitor_image: entry.profile_image || "",
                uniqueHash: crypto.randomUUID(),
              });
            });

            const processedLinkData = analytics.helpers.linkGenerator({ analyticsLinks: data, type: "detail" });
            setState({ config, data: processedLinkData.links });
          }
        });
      },
    },

    switchView: ({ view, data }) => {
      setViewType(view);
      setSelectedData(data);
    },
  };

  useEffect(() => {
    const config = analytics.meta.config[tabType];
    if (viewType === "group") {
      setState({ config: config["group"], data });
    }
    if (viewType === "detail") {
      if (!selectedData) return;
      const serviceName = [VIEWERS, BLOCKS, COUNTRIES, LINKS].find(e => e.SLUG === tabType)?.API_NAME;
      analytics.fetchDetailListData[serviceName]({ selectedData, config: config["detail"] });
    }
  }, [tabType, viewType, data, selectedData]);

  return (
    <>
      <div className={cx(style["table-info-wrap"], style["h-100"])}>
        {state?.data?.length > 0 && (
          <div
            className={cx(
              style["table-responsive-custom"],
              style["analytics-table"],
              style["customScroll"],
              style["scroll-X"]
            )}>
            <table
              id="analytics-table"
              ref={tableRef}
              className={cx(style["table"], style["table-borderless"], {
                [style["table-12-hours"]]: timeFormat === "12 Hours",
                [style["column4"]]: state?.config?.length === 4,
                [style["column3"]]: state?.config?.length === 3,
                [style["column2"]]: state?.config?.length === 2,
              })}>
              <thead>
                <tr>
                  {state.config.map((field, idx) => (
                    <th key={`header-col-${idx}`}>
                      {field.headerArrowRequired && (
                        <span
                          className={cx(style["cursor-pointer"], style["me-2"])}
                          onClick={() => analytics.switchView({ view: "group", data: null })}>
                          <Icon icon="ui-arrow-left3" additionalclass={"font-14 color-black"} />
                        </span>
                      )}
                      {field.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className={cx(style["customScroll"], style["scroll-Y"])}>
                {state.data.map((rowData, i) => (
                  <tr
                    key={`body-row-${i}`}
                    className={cx({
                      [style["cursor-pointer"]]:
                        viewType === "group" && !(tabType === LINKS.SLUG && !rowData?.is_analytics_present),
                    })}
                    onClick={() => {
                      if (viewType === "group" && !(tabType === LINKS.SLUG && !rowData?.is_analytics_present)) {
                        analytics.switchView({ view: "detail", data: rowData });
                      }
                    }}>
                    {state.config.map((field, j) => (
                      <td
                        key={`body-col-${j}`}
                        className={cx(
                          { [style["views"]]: field.type === "views" },
                          { [style["clicks"]]: field.type === "click" }
                        )}>
                        <div className={cx(style["d-flex"], style["align-items-center"])}>
                          <div className={cx({ [style["disable"]]: rowData.is_deleted })}>
                            <TableFormatter type={field.type} slug={field.slug} rowNo={i + 1} data={rowData} />
                          </div>

                          {field.arrowRequired && !(tabType === LINKS.SLUG && !rowData?.is_analytics_present) && (
                            <span
                              className={cx(style["cursor-pointer"], style["ms-auto"])}
                              onClick={() => analytics.switchView({ view: "detail", data: rowData })}>
                              <Icon icon="ui-arrow-right3" additionalclass={"font-14"} />
                            </span>
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

AnalyticsLoader.propTypes = {
  row: PropTypes.number.isRequired,
  col: PropTypes.number.isRequired,
  analytics: PropTypes.object.isRequired,
  colWidth: PropTypes.string,
};

ViewersSection.propTypes = {
  documentId: PropTypes.string.isRequired,
  config: PropTypes.object.isRequired,
  helpers: PropTypes.object.isRequired,
  tabType: PropTypes.string.isRequired,
  viewType: PropTypes.string.isRequired,
  setViewType: PropTypes.func.isRequired,
  data: PropTypes.array.isRequired,
};

AnalyticsModal.propTypes = {
  document: PropTypes.object.isRequired,
  _helpers: PropTypes.object.isRequired,
};

export default AnalyticsModal;
